import React from "react";
import { StyleSheet, Text, View, Linking } from "react-native";
import alert from "../../common/alert";
import type { Habit, HabitType } from "../types";
import LabelButton from "../../common/LabelButton";
import Introduction from "./Introduction";
import { useNavigate } from "react-router-dom";

export type StateProps = {
  isOwner: boolean;
  habit: Habit;
  isIntroEnabled: boolean;
  isInstalled: boolean;
  showManageOptions: boolean;
};
export type DispatchProps = {
  deleteHabit: (arg0: Habit) => void;
  installHabit: (arg0: string) => void;
  removeHabit: (arg0: string) => void;
};
export type OwnProps = {};
export type Props = StateProps & DispatchProps & OwnProps;

function ExtrasView({ habit }) {
  switch (habit.type) {
    case "freeform": {
      return (
        <View>
          <Text style={styles.promptsHeader}>Prompts</Text>
          {habit.extras.map((prompt) => {
            return (
              <Text key={prompt} style={styles.prompt}>
                {prompt}
              </Text>
            );
          })}
        </View>
      );
    }

    case "multi-day": {
      let dayViews = habit.extras.map((day, idx) => {
        return (
          <View key={day.text + " " + idx} style={styles.dayContainer}>
            <Text style={styles.dayText}>
              <Text style={styles.daySubTex}>Day {idx + 1}:</Text> {day.text}
            </Text>
            {day.prompts.map((prompt) => {
              return (
                <Text key={prompt} style={styles.dayPrompt}>
                  {prompt}
                </Text>
              );
            })}
          </View>
        );
      });
      return <View>{dayViews}</View>;
    }

    case "single-site": {
      return (
        <View>
          <Text
            style={{
              color: "blue",
            }}
            onPress={() => Linking.openURL(habit.extras.url)}
          >
            {habit.extras.url}
          </Text>
        </View>
      );
    }
  }

  return null;
}

function getTypeString(type: HabitType) {
  switch (type) {
    case "freeform":
      return "Daily";

    case "multi-day":
      return "Multi Day";

    case "single-site":
      return "Site";
  }

  return "";
}

export default function ViewHabit(props: Props) {
  const nav = useNavigate();
  const habit = props.habit;
  const title = habit.title;
  const type = getTypeString(habit.type);
  const description = habit.description;
  const actionButtons = [];

  if (props.isOwner) {
    const confirmDelete = () => {
      alert(
        "Are You Sure?",
        'Do you want to permanently delete "' + title + '"',
        [
          {
            text: "Cancel",
            onPress: () => {},
            style: "cancel",
          },
          {
            text: "Delete",
            onPress: () => {
              nav("/manage");
              props.deleteHabit(props.habit);
            },
          },
        ],
        {
          cancelable: false,
        }
      );
    };

    const navigateToEdit = () => {
      const { userId, createdms } = props.habit;
      const [userId1, userId2] = userId.split(":");
      nav("/edit/" + userId1 + "/" + userId2 + "/" + createdms);
    };

    actionButtons.push(
      <LabelButton
        key="delete"
        label="Delete"
        onPress={confirmDelete}
        type="negative"
      />
    );
    actionButtons.push(
      <LabelButton
        key="edit"
        label="Edit"
        onPress={navigateToEdit}
        type="positive"
      />
    );
  }

  if (props.showManageOptions) {
    if (props.isInstalled) {
      actionButtons.push(
        <LabelButton
          key="uninstall"
          label="Uninstall"
          onPress={() => props.removeHabit(habit.habitId)}
          type="neutral"
        />
      );
    } else {
      actionButtons.push(
        <LabelButton
          key="install"
          label="Install"
          onPress={() => props.installHabit(habit.habitId)}
          type="neutral"
        />
      );
    }
  }

  const introView = props.isIntroEnabled ? <Introduction /> : null;
  return (
    <View style={styles.container}>
      {introView}
      <View
        style={{
          padding: 8,
        }}
      >
        <Text style={styles.title}>{title}</Text>
        <Text>{description}</Text>
        <Text style={styles.type}>{type}:</Text>
        <ExtrasView habit={habit} />
      </View>
      <View
        style={{
          flexDirection: "row",
        }}
      >
        {actionButtons}
      </View>
    </View>
  );
}
const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "stretch",
    alignSelf: "center",
    maxWidth: 600,
    width: "100%",
  },
  hContainer: {
    flexDirection: "row",
  },
  title: {
    fontSize: 20,
    paddingTop: 5,
    paddingBottom: 5,
  },
  promptsHeader: {
    fontWeight: "bold",
    paddingTop: 5,
    paddingBottom: 5,
  },
  prompt: {
    paddingTop: 5,
    paddingBottom: 5,
  },
  dayText: {
    paddingTop: 5,
    paddingBottom: 5,
    fontWeight: "bold",
  },
  daySubText: {
    fontWeight: "light",
  },
  dayPrompt: {
    paddingTop: 5,
    paddingLeft: 10,
    paddingBottom: 5,
  },
  dayContainer: {
    borderWidth: 1,
    borderColor: "#888888",
    borderRadius: 5,
    margin: 4,
    padding: 4,
  },
  type: {
    fontWeight: "bold",
  },
});
